import api from "../../../../services/api";

const getSignature = () => localStorage.getItem("signature");

export const listEmailTemplates = async (
  companyId: string,
  companyType: string,
  category?: string,
) => {
  const response = await api.post("emailService/email-templates/list", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...(category ? { category } : {}),
  });
  return response.data?.data;
};

export const createEmailTemplate = async (
  companyId: string,
  companyType: string,
  template: Record<string, any>,
) => {
  const response = await api.post("emailService/email-templates/create", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...template,
  });
  return response.data?.data;
};

export const updateEmailTemplate = async (
  companyId: string,
  companyType: string,
  templateId: number,
  template: Record<string, any>,
) => {
  const response = await api.post("emailService/email-templates/update", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    template_id: templateId,
    ...template,
  });
  return response.data?.data;
};

export const deleteEmailTemplate = async (
  companyId: string,
  companyType: string,
  templateId: number,
) => {
  const response = await api.post("emailService/email-templates/delete", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    template_id: templateId,
  });
  return response.data?.data;
};

export const previewEmailTemplate = async (
  companyId: string,
  companyType: string,
  templateId: number,
  sampleData: Record<string, any> = {},
) => {
  const response = await api.post("emailService/email-templates/preview", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    template_id: templateId,
    sample_data: sampleData,
  });
  return response.data?.data;
};

export const getEmailTemplateVariables = async (
  companyId: string,
  companyType: string,
  templateId: number,
) => {
  const response = await api.post("emailService/email-templates/variables", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    template_id: templateId,
  });
  return response.data?.data;
};

export const testSendEmailTemplate = async (
  companyId: string,
  companyType: string,
  templateId: number,
  toEmail: string,
  sampleData: Record<string, any> = {},
) => {
  const response = await api.post(
    "emailService/email-templates/test-send",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      template_id: templateId,
      to_email: toEmail,
      sample_data: sampleData,
    },
  );
  return response.data?.data;
};

export const checkTemplateCodeAvailability = async (
  companyId: string,
  companyType: string,
  templateCode: string,
  templateId?: number,
) => {
  const response = await api.post(
    "emailService/email-templates/check-code-availability",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      template_code: templateCode,
      ...(templateId ? { template_id: templateId } : {}),
    },
  );
  return response.data?.data as
    | { available: boolean; template_code: string; conflicting_template_name?: string | null }
    | undefined;
};

export interface EmailTemplateVersion {
  id: number;
  template_id: number;
  version: number;
  template_name: string;
  template_code: string;
  subject: string;
  body_html: string;
  available_variables?: string[] | null;
  category?: string | null;
  change_note?: string | null;
  created_at: string;
}

export const listEmailTemplateVersions = async (
  companyId: string,
  companyType: string,
  templateId: number,
) => {
  const response = await api.post("emailService/email-templates/versions", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    template_id: templateId,
  });
  return (response.data?.data as EmailTemplateVersion[]) || [];
};

export const restoreEmailTemplateVersion = async (
  companyId: string,
  companyType: string,
  templateId: number,
  versionId: number,
) => {
  const response = await api.post(
    "emailService/email-templates/restore-version",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      template_id: templateId,
      version_id: versionId,
    },
  );
  return response.data?.data;
};

export const renderEmailTemplate = async (
  companyId: string,
  companyType: string,
  payload: { template_id?: number; template_code?: string; variables?: Record<string, any> },
) => {
  const response = await api.post("emailService/email-templates/render", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });
  return response.data?.data as
    | { template_id: number; template_code: string; subject: string; html: string }
    | undefined;
};

export interface EmailTemplateStatsRow {
  template_id: number;
  template_code: string;
  delivered: number;
  failed: number;
  total: number;
}

export const getEmailTemplateStats = async (
  companyId: string,
  companyType: string,
  days = 30,
) => {
  const response = await api.post("emailService/email-templates/stats", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    days,
  });
  return response.data?.data as
    | { since: string; days: number; stats: EmailTemplateStatsRow[] }
    | undefined;
};

export interface EmailEventRoute {
  id: number;
  company_id?: number | null;
  company_type?: string | null;
  source_file: string;
  template_code: string;
  label?: string | null;
  is_active: boolean;
  is_default: boolean;
}

export const listEventRoutes = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("emailService/email-event-routes/list", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
  });
  return (response.data?.data as EmailEventRoute[]) || [];
};

export const upsertEventRoute = async (
  companyId: string,
  companyType: string,
  payload: {
    source_file: string;
    template_code: string;
    label?: string;
    is_active?: boolean;
  },
) => {
  const response = await api.post(
    "emailService/email-event-routes/upsert",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      ...payload,
    },
  );
  return response.data?.data;
};

export const deleteEventRoute = async (
  companyId: string,
  companyType: string,
  routeId: number,
) => {
  const response = await api.post(
    "emailService/email-event-routes/delete",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      route_id: routeId,
    },
  );
  return response.data?.data;
};

export const listEmailTemplatesByCategory = async (
  companyId: string,
  companyType: string,
  category: string,
) => {
  const response = await api.post("emailService/email-templates/by-category", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    category,
  });
  return response.data?.data;
};
